import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/atoms/form";
import DatePicker from "~/molecules/DatePicker";
import { addDays } from "~/helpers/date";
import {
  type DocumentVersionEditFormValues,
  type DocumentVersionField,
} from "~/hooks/useDocumentVersionEditForm";

interface Props {
  field?: DocumentVersionField;
}

export const DocumentVersionReviewDateFields = ({ field }: Props) => {
  const { t } = useTranslation();
  const form = useFormContext<DocumentVersionEditFormValues>();
  const reviewFrom = useWatch({
    control: form.control,
    name: "reviewFrom",
  });

  const handleReviewFromChange = (date?: Date) => {
    const dateValue = date ?? null;
    form.setValue("reviewFrom", dateValue);

    if (date) {
      const currentReviewTo = form.getValues("reviewTo");

      if (currentReviewTo && currentReviewTo < date) {
        form.setValue("reviewTo", null);
      }
    }
  };

  return (
    <div className="flex gap-4">
      {field === "adoptedAt" && (
        <FormField
          control={form.control}
          name="adoptedAt"
          render={({ field: formField }) => (
            <FormItem className="flex-1">
              <FormLabel>{t(($) => $.commons.document.adoptedAt)}</FormLabel>
              <FormControl>
                <DatePicker
                  onChange={(date) => formField.onChange(date ?? null)}
                  value={formField.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {(field === undefined || field === "reviewFrom") && (
        <FormField
          control={form.control}
          name="reviewFrom"
          render={({ field: formField }) => (
            <FormItem className="flex-1">
              <FormLabel>{t(($) => $.commons.document.reviewFrom)}</FormLabel>
              <FormControl>
                <DatePicker
                  onChange={handleReviewFromChange}
                  value={formField.value}
                  minDate={new Date()}
                  maxDate={form.getValues("reviewTo") ?? undefined}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {(field === undefined || field === "reviewTo") && (
        <FormField
          control={form.control}
          name="reviewTo"
          render={({ field: formField }) => (
            <FormItem className="flex-1">
              <FormLabel>{t(($) => $.commons.document.reviewTo)}</FormLabel>
              <FormControl>
                <DatePicker
                  onChange={(date) => formField.onChange(date ?? null)}
                  value={formField.value}
                  minDate={reviewFrom ? addDays(reviewFrom, 1) : new Date()}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
